import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  UserGroupIcon,
  VideoCameraIcon,
  CheckIcon,
  LockClosedIcon,
  InformationCircleIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline';
import { useApi, type User, type Group, type CameraRef } from '../api';
import { usePermissions } from '../auth';
import GroupPickerPanel from '../components/groups/GroupPickerPanel';
import { PageTitle } from '../components/layout/TitleBar';

export interface AccessControlPageProps {
  /** Root container classes. */
  className?: string;
  /** Require a selected node (cloud, multi-node). When set with no nodeKey, prompts to pick one. */
  requireNode?: boolean;
  /** The selected node id (cloud). Changing it refetches; adapters bind it internally. */
  nodeKey?: string | null;
  /** Label for the node concept when node-scoped (cloud). */
  nodeLabel?: string;
  /** Permission required to save; when set, the Save button is gated on it. */
  editPermission?: string;
}

/** Flatten a (possibly nested) group list into the flat `parent_id` list the picker wants. */
function flattenGroups(nodes: Group[]): Group[] {
  const out: Group[] = [];
  const walk = (list: Group[]) => {
    for (const n of list) {
      out.push(n);
      if (n.children && n.children.length) walk(n.children);
    }
  };
  walk(Array.isArray(nodes) ? nodes : []);
  return out;
}

/**
 * Access Control — assign camera GROUPS and individual CAMERAS to a user.
 * A user's accessible cameras = union(group-derived, directly-assigned), enforced
 * by the backend for the camera list, live (ULLFU) and recordings.
 *
 * Shared by both dashboards via useApi().accessControl. Single-node hosts (local)
 * omit `listNodes`; multi-node hosts (cloud) provide it and the page shows a node
 * picker, threading the selected node id through every call.
 */
export default function AccessControlPage({
  className = 'space-y-3',
  requireNode = false,
  nodeKey,
  nodeLabel = 'Node',
  editPermission,
}: AccessControlPageProps) {
  const { accessControl: api } = useApi();
  const { hasPermission } = usePermissions();
  const canEdit = !editPermission || hasPermission(editPermission);
  const noNode = requireNode && !nodeKey;

  const [users, setUsers] = useState<User[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const [allGroups, setAllGroups] = useState<Group[]>([]);
  const [allCameras, setAllCameras] = useState<CameraRef[]>([]);
  const [cameraSearch, setCameraSearch] = useState('');

  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [cameraIds, setCameraIds] = useState<string[]>([]);
  // Cameras reachable through the selected groups (incl. sub-groups) — locked,
  // mirroring the "inherited from group" lock on the Model Assignment page.
  const [groupCameraIds, setGroupCameraIds] = useState<Set<string>>(new Set());

  const [loadingLists, setLoadingLists] = useState(false);
  const [loadingAccess, setLoadingAccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);

  // ── Load users + groups + cameras (refetch when the selected node changes) ──
  useEffect(() => {
    setSelectedUser(null);
    setGroupIds([]); setCameraIds([]); setGroupCameraIds(new Set());
    if (noNode) { setUsers([]); setAllGroups([]); setAllCameras([]); return; }
    setLoadingLists(true);
    Promise.all([api.listUsers(), api.listGroups(), api.listCameras()])
      .then(([us, gs, cs]) => {
        // Active users only — deactivated/deleted users are not assignable.
        setUsers((us || []).filter((u) => u.is_active !== false));
        setAllGroups(flattenGroups(gs || []));
        setAllCameras(cs || []);
      })
      .catch(() => setFeedback({ kind: 'err', msg: 'Failed to load access data' }))
      .finally(() => setLoadingLists(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeKey, noNode]);

  // ── Load the selected user's current access ──
  const selectUser = useCallback((user: User) => {
    setSelectedUser(user);
    setDirty(false);
    setFeedback(null);
    setLoadingAccess(true);
    api.getAccess(user.id)
      .then((sel) => {
        setGroupIds(sel.groupIds || []);
        setCameraIds(sel.cameraIds || []);
      })
      .catch(() => setFeedback({ kind: 'err', msg: 'Failed to load user access' }))
      .finally(() => setLoadingAccess(false));
  }, [api]);

  // ── Resolve selected groups → their cameras (recursive) so we can lock them ──
  useEffect(() => {
    if (groupIds.length === 0) { setGroupCameraIds(new Set()); return; }
    let cancelled = false;
    Promise.all(groupIds.map((gid) => api.groupCameras(gid).catch(() => [])))
      .then((lists) => {
        if (cancelled) return;
        setGroupCameraIds(new Set(lists.flat().map((c: CameraRef) => c.id)));
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupIds]);

  const onGroupsChange = (ids: string[]) => { setGroupIds(ids); setDirty(true); };
  const toggleCamera = (id: string) => {
    if (groupCameraIds.has(id)) return; // locked — covered by a group
    setCameraIds((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
    setDirty(true);
  };

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q));
  }, [users, userSearch]);

  const filteredCameras = useMemo(() => {
    const q = cameraSearch.trim().toLowerCase();
    if (!q) return allCameras;
    return allCameras.filter((c) => (c.name || '').toLowerCase().includes(q));
  }, [allCameras, cameraSearch]);

  const directCameraCount = useMemo(
    () => cameraIds.filter((id) => !groupCameraIds.has(id)).length,
    [cameraIds, groupCameraIds],
  );

  const save = async () => {
    if (!selectedUser) return;
    setSaving(true);
    setFeedback(null);
    try {
      // Don't persist direct grants for cameras already covered by a group.
      const directCameraIds = cameraIds.filter((id) => !groupCameraIds.has(id));
      await api.setAccess(selectedUser.id, { groupIds, cameraIds: directCameraIds });
      setDirty(false);
      setFeedback({ kind: 'ok', msg: `Access updated for ${selectedUser.name || selectedUser.email}` });
    } catch (e: any) {
      setFeedback({ kind: 'err', msg: e?.message || 'Failed to save access' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={className}>
      <PageTitle title="Access Control" subtitle="Users see and access only the camera groups and cameras assigned to them" />

      {noNode && (
        <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
          <ServerStackIcon className="h-5 w-5" />
          Select a {nodeLabel.toLowerCase()} to manage its users' camera access.
        </div>
      )}

      {!noNode && (<>
      {/* ── How it works ── */}
      <div data-tour="access-control-help" className="rounded-lg border border-border bg-muted/30 p-3 text-[12px] text-muted-foreground">
        <div className="mb-1.5 flex items-center gap-1.5 font-medium text-foreground">
          <InformationCircleIcon className="h-4 w-4" /> How access works
        </div>
        <ul className="list-disc space-y-1 pl-5">
          <li>A user's accessible cameras = <span className="font-medium">cameras from assigned groups</span> + <span className="font-medium">directly-assigned cameras</span>. Enforced for the camera list, live view, and recordings.</li>
          <li>Assigning a <span className="font-medium">parent group</span> automatically includes all of its sub-groups and their cameras.</li>
          <li>Cameras covered by an assigned group show as
            <LockClosedIcon className="mx-1 inline h-3 w-3 align-text-bottom" /><span className="font-medium">via group</span> and are locked —
            to remove one, remove it from the group rather than toggling it here.</li>
          <li>Only <span className="font-medium">active</span> users appear here.{requireNode ? ` These are the cloud users granted access to this ${nodeLabel.toLowerCase()}.` : ''}</li>
        </ul>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-3">
        {/* ── Left: user list ── */}
        <div data-tour="access-control-users" className="rounded-lg border border-border bg-card flex flex-col max-h-[calc(100vh-5.5rem)]">
          <div className="p-3 border-b border-border">
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search users…"
                className="w-full rounded-md border border-border bg-background py-1.5 pl-8 pr-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-[var(--brand)]"
              />
            </div>
          </div>
          <div className="overflow-y-auto">
            {loadingLists && <p className="p-4 text-sm text-muted-foreground">Loading…</p>}
            {!loadingLists && filteredUsers.length === 0 && (
              <p className="p-4 text-sm text-muted-foreground">No users found.</p>
            )}
            {filteredUsers.map((u) => {
              const active = selectedUser?.id === u.id;
              return (
                <button
                  key={u.id}
                  onClick={() => selectUser(u)}
                  className={`w-full text-left px-3 py-2.5 border-b border-border transition-colors ${
                    active ? 'bg-muted' : 'hover:bg-muted/60'
                  }`}
                  style={active ? { borderLeft: '3px solid var(--brand)' } : { borderLeft: '3px solid transparent' }}
                >
                  <div className="text-sm font-medium text-foreground truncate">{u.name || u.email}</div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {u.email}{u.role ? ` · ${u.role}` : ''}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Right: access editor ── */}
        <div data-tour="access-control-editor" className="rounded-lg border border-border bg-card min-h-[300px]">
          {!selectedUser ? (
            <div className="flex h-full items-center justify-center p-10 text-sm text-muted-foreground">
              Select a user to manage their camera access.
            </div>
          ) : (
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between gap-3 p-4 border-b border-border">
                <div>
                  <div className="text-sm font-semibold text-foreground">
                    Access for {selectedUser.name || selectedUser.email}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {groupIds.length} group{groupIds.length === 1 ? '' : 's'}
                    {groupCameraIds.size > 0 && <> · {groupCameraIds.size} via group{groupCameraIds.size === 1 ? '' : 's'}</>}
                    {' · '}{directCameraCount} direct camera{directCameraCount === 1 ? '' : 's'}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {feedback && (
                    <span className={`text-[11px] ${feedback.kind === 'ok' ? 'text-emerald-600 dark:text-emerald-400' : 'text-destructive'}`}>
                      {feedback.msg}
                    </span>
                  )}
                  <button
                    onClick={save}
                    disabled={!dirty || saving || loadingAccess || !canEdit}
                    title={!canEdit ? 'You do not have permission to edit access' : undefined}
                    className="inline-flex items-center gap-1.5 rounded-md bg-[var(--brand)] px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 transition-opacity"
                  >
                    <CheckIcon className="h-4 w-4" />
                    {saving ? 'Saving…' : 'Save changes'}
                  </button>
                </div>
              </div>

              {loadingAccess ? (
                <div className="p-6 text-sm text-muted-foreground">Loading access…</div>
              ) : (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 p-4 overflow-y-auto">
                  {/* Groups */}
                  <div>
                    <div className="mb-2 flex items-center gap-1.5 text-sm font-medium text-foreground">
                      <UserGroupIcon className="h-4 w-4" /> Camera Groups
                    </div>
                    <GroupPickerPanel
                      groups={allGroups}
                      selectedIds={groupIds}
                      onChange={onGroupsChange}
                      label="Group Access"
                      multiple
                    />
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      Access to a parent group automatically includes all its sub-groups.
                    </p>
                  </div>

                  {/* Cameras */}
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                        <VideoCameraIcon className="h-4 w-4" /> Individual Cameras
                      </span>
                      <div className="flex gap-2 text-[11px]">
                        <button className="text-[var(--brand)] hover:underline"
                          onClick={() => { setCameraIds(allCameras.map((c) => c.id).filter((id) => !groupCameraIds.has(id))); setDirty(true); }}>
                          Select all
                        </button>
                        <button className="text-muted-foreground hover:underline"
                          onClick={() => { setCameraIds([]); setDirty(true); }}>
                          Clear
                        </button>
                      </div>
                    </div>
                    <div className="relative mb-2">
                      <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <input
                        value={cameraSearch}
                        onChange={(e) => setCameraSearch(e.target.value)}
                        placeholder="Search cameras…"
                        className="w-full rounded-md border border-border bg-background py-1.5 pl-8 pr-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-[var(--brand)]"
                      />
                    </div>
                    <div className="max-h-[360px] overflow-y-auto rounded-md border border-border divide-y divide-border">
                      {filteredCameras.length === 0 && (
                        <p className="p-3 text-sm text-muted-foreground">No cameras found.</p>
                      )}
                      {filteredCameras.map((c) => {
                        const locked = groupCameraIds.has(c.id);
                        return (
                          <label
                            key={c.id}
                            title={locked ? 'Included via an assigned group — manage it from the group' : c.name}
                            className={`flex items-center gap-2.5 px-3 py-2 ${
                              locked ? 'bg-muted/40 cursor-default' : 'cursor-pointer hover:bg-muted/60'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={locked || cameraIds.includes(c.id)}
                              disabled={locked}
                              onChange={() => toggleCamera(c.id)}
                              className="h-4 w-4 rounded border-border text-[var(--brand)] focus:ring-[var(--brand)] disabled:opacity-100"
                            />
                            <span className={`text-sm truncate ${locked ? 'text-muted-foreground' : 'text-foreground'}`}>
                              {c.name}
                            </span>
                            {locked && (
                              <span className="ml-auto inline-flex flex-shrink-0 items-center gap-1 text-[10px] text-muted-foreground">
                                <LockClosedIcon className="h-3 w-3" /> via group
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      Directly-granted cameras add on top of group access. Cameras marked
                      <LockClosedIcon className="mx-1 inline h-3 w-3 align-text-bottom" />
                      <span className="font-medium">via group</span> are already covered by an assigned group.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      </>)}
    </div>
  );
}
